/**
 * WEB COPY of LettersHomeScreen.tsx — picked by the web bundler ahead of the
 * mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Same cards and actions as the phone. The one difference is where the
 * recording counts come from: the phone reads its own encrypted registry,
 * which the browser does not have. The web reads two server lists instead —
 * uploaded recordings (letter tier+) and the on-device notes the phone
 * sends when a reading is kept — so a reader who recorded on their phone
 * sees "1 reading on your iPhone" here. When LettersHomeScreen.tsx changes,
 * re-apply the change here.
 */
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import {
  letterToLovedOnesService,
  LetterToLovedOnesRec,
} from '../../services/letterToLovedOnesInfo';
import {
  letterDeviceRecordingNotes,
  DeviceRecordingNote,
} from '../../services/media/letterDeviceRecordingNotes';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('LettersHome');

const deliveryLine = (letter: LetterToLovedOnesRec): string => {
  switch (letter.deliveryTrigger) {
    case 'eol': {
      const hours = letter.releaseDelayHours ?? 0;
      if (hours >= 24) {
        const days = Math.round(hours / 24);
        const label = days >= 365 ? '1 year' : `${days} days`;
        return `Delivered ${label} after you're gone`;
      }
      return "Delivered after you're gone";
    }
    case 'date':
      return letter.releaseAt
        ? `Sealed until ${new Date(letter.releaseAt).toLocaleDateString()}`
        : 'Sealed until the date you chose';
    case 'manual':
      return 'Released only when you say so';
    default:
      return 'Delivery not set up yet';
  }
};

/** "Matt's iPhone (iPhone 15)" → "Matt's iPhone"; empty → "your phone". */
export const shortDeviceName = (name: string | undefined): string => {
  const trimmed = (name ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  return trimmed || 'your phone';
};

export const LettersHomeScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const [letters, setLetters] = useState<LetterToLovedOnesRec[]>([]);
  const [cloudCounts, setCloudCounts] = useState<Record<string, number>>({});
  const [phoneNotes, setPhoneNotes] = useState<DeviceRecordingNote[]>([]);
  const [grantees, setGrantees] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const load = async () => {
        let allLetters: LetterToLovedOnesRec[] = [];
        try {
          const [fetched, notes] = await Promise.all([
            letterToLovedOnesService.getAllLettersToLovedOnes(),
            letterDeviceRecordingNotes.list(),
          ]);
          if (cancelled) return;
          allLetters = fetched;
          setLetters(fetched);
          setPhoneNotes(notes);
        } catch (error) {
          logger.error('Failed to load letters home:', error);
        } finally {
          if (!cancelled) setLoading(false);
        }
        // Counts and grants are nice-to-haves — load separately so a
        // failure never blanks the letter list.
        try {
          const saved = allLetters.filter((l) => (l.status ?? 'saved') === 'saved');
          const counts = await Promise.all(
            saved.map(async (l) => [l.id, (await letterDeviceRecordingNotes.listCloud(l.id)).length] as const),
          );
          if (!cancelled) setCloudCounts(Object.fromEntries(counts));
        } catch (error) {
          logger.debug('Cloud recording counts unavailable:', error);
        }
        try {
          const byLetter = await letterToLovedOnesService.getDeliveryGrantsByLetter();
          if (!cancelled) setGrantees(byLetter);
        } catch (error) {
          logger.debug('Delivery grants unavailable:', error);
        }
      };
      load();
      return () => {
        cancelled = true;
      };
    }, []),
  );

  const openLetter = (letter: LetterToLovedOnesRec) => {
    if ((letter.status ?? 'saved') === 'draft') {
      navigation.navigate('LetterCanvas', { letterId: letter.id });
    } else {
      navigation.navigate('LetterSaved', { letterId: letter.id });
    }
  };

  const titleFor = (letter: LetterToLovedOnesRec): string => {
    const who = letter.salutation || letter.recipientName;
    return who ? `To ${who}` : 'Your letter';
  };

  const granteeLine = (letterId: string): string | null => {
    const emails = grantees[letterId];
    if (!emails || emails.length === 0) return null;
    const shown = emails.slice(0, 2).join(', ');
    const extra = emails.length - 2;
    return `Goes to ${shown}${extra > 0 ? ` +${extra} more` : ''}`;
  };

  const phoneLine = (letterId: string): string | null => {
    const notes = phoneNotes.filter((n) => n.letterId === letterId);
    if (notes.length === 0) return null;
    const device = shortDeviceName(notes[0].deviceName);
    return `${notes.length} reading${notes.length === 1 ? '' : 's'} on ${device}`;
  };

  const goBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('LetterLevels');
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={goBack}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Your Letters</Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        {loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color="#29BFC0" size="large" />
          </View>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false}>
            {letters.length === 0 && (
              <Text style={styles.emptyText}>
                No letters yet. The first one is the hardest — and the most
                important.
              </Text>
            )}

            {letters.map((letter) => {
              const isDraft = (letter.status ?? 'saved') === 'draft';
              const cloudCount = cloudCounts[letter.id] ?? 0;
              const onPhone = phoneLine(letter.id);
              const whoGets = granteeLine(letter.id);
              // The card is tappable but not a <button>: it holds the two
              // action buttons, and HTML forbids a button inside a button.
              return (
                <TouchableOpacity
                  key={letter.id}
                  style={styles.card}
                  onPress={() => openLetter(letter)}
                  accessibilityLabel={`${titleFor(letter)}${isDraft ? ', draft' : ''}`}
                >
                  <View style={styles.cardHeader}>
                    <Text style={styles.cardTitle}>{titleFor(letter)}</Text>
                    {isDraft && (
                      <View style={styles.draftChip}>
                        <Text style={styles.draftChipText}>Draft</Text>
                      </View>
                    )}
                  </View>

                  <Text style={styles.deliveryLine}>{deliveryLine(letter)}</Text>
                  {whoGets && <Text style={styles.granteeLine}>{whoGets}</Text>}
                  {cloudCount > 0 && (
                    <Text style={styles.recordingLine}>
                      {cloudCount} voice recording{cloudCount === 1 ? '' : 's'} · backed up
                    </Text>
                  )}
                  {onPhone && <Text style={styles.recordingLine}>{onPhone}</Text>}

                  <View style={styles.actionsRow}>
                    <TouchableOpacity
                      style={styles.actionLink}
                      onPress={() =>
                        navigation.navigate('LetterCanvas', { letterId: letter.id })
                      }
                      accessibilityRole="button"
                      accessibilityLabel="Edit this letter"
                    >
                      <Text style={styles.actionLinkText}>
                        {isDraft ? 'Continue writing' : 'Edit letter'}
                      </Text>
                    </TouchableOpacity>
                    {!isDraft && (
                      <TouchableOpacity
                        style={styles.actionLink}
                        onPress={() => navigation.navigate('LetterSaved', { letterId: letter.id })}
                        accessibilityRole="button"
                        accessibilityLabel="Recordings, export, and delivery"
                      >
                        <Text style={styles.actionLinkText}>Recordings & delivery</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}

            <TouchableOpacity
              style={styles.addButton}
              onPress={() => navigation.navigate('LetterRecipient')}
              accessibilityRole="button"
              accessibilityLabel="Write another letter"
            >
              <Text style={styles.addButtonText}>+ Write another letter</Text>
            </TouchableOpacity>
            <Text style={styles.footnote}>
              Writing, recording, and keeping letters is free forever.
            </Text>

            <View style={styles.bottomPadding} />
          </ScrollView>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginTop: ms(24),
    marginBottom: ms(16),
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(10),
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardTitle: {
    flex: 1,
    fontSize: ms(16, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
  },
  draftChip: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(10),
    paddingHorizontal: ms(8),
    paddingVertical: ms(2),
    marginLeft: ms(8),
  },
  draftChipText: {
    color: '#FFFFFF',
    fontSize: ms(11, 0.3),
    fontWeight: '700',
  },
  deliveryLine: {
    fontSize: ms(13, 0.3),
    color: '#2C4364',
    marginTop: ms(6),
  },
  granteeLine: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  recordingLine: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  actionsRow: {
    flexDirection: 'row',
    gap: ms(16),
    marginTop: ms(12),
  },
  actionLink: {
    paddingVertical: ms(2),
  },
  actionLinkText: {
    color: '#29BFC0',
    fontSize: ms(13, 0.3),
    fontWeight: '600',
  },
  addButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
    borderRadius: ms(25),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(15, 0.3),
    fontWeight: '600',
  },
  footnote: {
    fontSize: ms(12, 0.3),
    color: '#A9A9A9',
    textAlign: 'center',
    marginTop: ms(8),
  },
  bottomPadding: {
    height: s(40),
  },
});

export default LettersHomeScreen;
